import { useTheme } from "@/lib/ground";
import { kitEdge, readableOn } from "@/lib/team-color";
import { cn } from "@/lib/utils";

/**
 * The board's code chip, for every page outside the board: the team's code
 * in NXT on the team's own colour, the box leaning like the
 * face (`.slant-chip`), the ink chosen to read on the kit (`readableOn`).
 * The public directory, a team's profile, a club's page and the team hub
 * all wore a round badge before the board redesign; a spectator who knows
 * NXT by its peach chip on the board should meet the same chip everywhere.
 *
 * `data-edge` is the kit's own call (`kitEdge`): a ring in the label's ink
 * when the painted fill is under 3:1 against the pane the current theme
 * paints - a white kit on the light theme's white card, a black one on the
 * dark glass - and nothing on a kit that already stands off it.
 */
export function TeamChip({
  short,
  color,
  size = "sm",
  className,
}: {
  short: string;
  color: string;
  size?: "sm" | "md" | "lg";
  className?: string | undefined;
}) {
  const ink = readableOn(color);
  const theme = useTheme();
  return (
    <span
      data-edge={kitEdge(color, theme)}
      className={cn(
        "slant-chip team-code inline-flex shrink-0 items-center justify-center leading-none",
        size === "lg"
          ? "min-w-[5rem] px-3 py-1.5 text-xl"
          : size === "md"
            ? "min-w-[3.5rem] px-2 py-1 text-sm"
            : "min-w-[2.75rem] px-1.5 py-0.5 text-xs adm:min-w-[3.25rem] adm:px-2",
        className,
      )}
      style={{ background: ink.bg, color: ink.fg }}
    >
      {short}
    </span>
  );
}
